import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
	getRehabTree,
	getRehabTreeStatus,
	getRehabTreeValidationStatus,
	setRefreshResults,
} from '@Actions';
import { success, error } from '@innovyze/stylovyze';
import { TREE_STATES } from '@Utils/TreeUtils';
import { useGlobalization } from '@Translations';

export interface UseTreeStatusPollingArgs {
	treeId: string;
	rehabTreeStatus: string | undefined;
	rehabTreeValidationStatus: string | undefined;
	hasFullAccess: boolean;
	hasViewerAccess: boolean;
	isLoading: boolean;
}

export interface UseTreeStatusPollingResult {
	isRehabTreeRunning: boolean;
	isRehabTreeValidating: boolean;
	isRehabTreeEditable: boolean;
	isDisabled: boolean;
}

export const useTreeStatusPolling = ({
	treeId,
	rehabTreeStatus,
	rehabTreeValidationStatus,
	hasFullAccess,
	hasViewerAccess,
	isLoading,
}: UseTreeStatusPollingArgs): UseTreeStatusPollingResult => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();

	const [checkTreeStatusCounter, setCheckTreeStatusCounter] =
		useState<number>(0);
	const [ticker, setTicker] = useState<NodeJS.Timeout>();

	const isRehabTreeRunning = rehabTreeStatus === TREE_STATES.IN_PROGRESS;
	const isRehabTreeValidating =
		rehabTreeValidationStatus === TREE_STATES.IN_PROGRESS;
	const isRehabTreeEditable =
		hasFullAccess && !isRehabTreeRunning && !isRehabTreeValidating;
	const isDisabled =
		hasViewerAccess ||
		isLoading ||
		isRehabTreeRunning ||
		isRehabTreeValidating;

	const refreshTreeRunStatus = () => {
		dispatch(
			getRehabTreeStatus({
				Id: treeId,
			}),
		);
	};

	const refreshTreeValidationStatus = () => {
		dispatch(
			getRehabTreeValidationStatus({
				Id: treeId,
			}),
		);
	};

	useEffect(() => {
		if (isRehabTreeRunning) {
			refreshTreeRunStatus();
			setTicker(
				setTimeout(() => {
					setCheckTreeStatusCounter(checkTreeStatusCounter + 1);
				}, 5000),
			);
		} else if (isRehabTreeValidating) {
			refreshTreeValidationStatus();
			setTicker(
				setTimeout(() => {
					setCheckTreeStatusCounter(checkTreeStatusCounter + 1);
				}, 5000),
			);
		}
	}, [checkTreeStatusCounter]);

	useEffect(() => {
		if (!ticker && isRehabTreeRunning) {
			setCheckTreeStatusCounter(1);
		} else if (ticker) {
			if (!isRehabTreeRunning) {
				clearTimeout(ticker);
				setTicker(undefined);
				setCheckTreeStatusCounter(0);
			}
			if (rehabTreeStatus === TREE_STATES.COMPLETE) {
				dispatch(
					getRehabTree({
						Id: treeId,
					}),
				);
				dispatch(setRefreshResults(true));
				dispatch(success(t('Tree run successful')));
			} else if (rehabTreeStatus === TREE_STATES.FAILED) {
				dispatch(error(t('Tree run failed')));
			}
		}
	}, [rehabTreeStatus]);

	useEffect(() => {
		if (!ticker && isRehabTreeValidating) {
			setCheckTreeStatusCounter(1);
		} else if (ticker) {
			if (!isRehabTreeValidating) {
				clearTimeout(ticker);
				setTicker(undefined);
				setCheckTreeStatusCounter(0);
			}
			if (rehabTreeValidationStatus === TREE_STATES.COMPLETE) {
				dispatch(
					getRehabTree({
						Id: treeId,
					}),
				);
				dispatch(success(t('Previewing asset count complete')));
			} else if (rehabTreeValidationStatus === TREE_STATES.FAILED) {
				dispatch(error(t('Previewing asset count has failed')));
				dispatch(
					getRehabTree({
						Id: treeId,
					}),
				);
			}
		}
	}, [rehabTreeValidationStatus]);

	return {
		isRehabTreeRunning,
		isRehabTreeValidating,
		isRehabTreeEditable,
		isDisabled,
	};
};
